import type { PostgrestError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { formatSupabaseAccessError } from "@/lib/api/videos";
import {
  buildLineupFromLegacy,
  deriveLegacyFromLineup,
  normalizeLineup,
  resolveBundleLineup,
  type BundleLineupItem,
} from "@/lib/bundleLineup";
import type {
  BodyRegionFocus,
  BundleStatus,
  QuarterlyBundleRow,
} from "@/types/database";

/** PostgREST/Postgres when DB schema never got `quarterly_bundles.archived_at` (curriculum redesign migration). */
function isMissingArchivedAtColumnError(error: unknown): boolean {
  const e = error as PostgrestError | null;
  const msg = (e?.message ?? "").toLowerCase();
  if (!msg.includes("archived_at")) return false;
  return (
    msg.includes("does not exist") ||
    msg.includes("schema cache") ||
    e?.code === "PGRST204"
  );
}

function isMissingLineupItemsColumnError(error: unknown): boolean {
  const e = error as PostgrestError | null;
  const msg = (e?.message ?? "").toLowerCase();
  if (!msg.includes("lineup_items")) return false;
  return (
    msg.includes("does not exist") ||
    msg.includes("schema cache") ||
    e?.code === "PGRST204"
  );
}

/** Normalize DB row so callers always see a resolved lineup_items array. */
function hydrateBundleRow(row: QuarterlyBundleRow): QuarterlyBundleRow {
  const lineup_items = resolveBundleLineup(row);
  const legacy = deriveLegacyFromLineup(lineup_items);
  return {
    ...row,
    lineup_items,
    intro_video_id: row.intro_video_id ?? legacy.intro_video_id,
    exercise_ids: Array.isArray(row.exercise_ids) ? row.exercise_ids : legacy.exercise_ids,
  };
}

export interface ListBundlesFilters {
  organization_id?: string;
  job_category_id?: string | null;
  quarter?: string;
  status?: BundleStatus;
  body_region_focus?: BodyRegionFocus;
  intro_video_id?: string;
  /** Match bundles whose lineup_items JSON contains this video ref (custom slots). */
  lineup_video_id?: string;
  curriculum_phase_id?: string;
  /** Default: hide archived bundles. Pass true to include them (HQ history view). */
  include_archived?: boolean;
}

function buildListBundlesQuery(filters: ListBundlesFilters | undefined, applyArchivedAtNull: boolean) {
  let q = supabase.from("quarterly_bundles").select("*").order("created_at", { ascending: false });
  if (filters?.organization_id) q = q.eq("organization_id", filters.organization_id);
  if (filters?.job_category_id === null) q = q.is("job_category_id", null);
  else if (typeof filters?.job_category_id === "string") q = q.eq("job_category_id", filters.job_category_id);
  if (filters?.quarter) q = q.eq("quarter", filters.quarter);
  if (filters?.status) q = q.eq("status", filters.status);
  if (filters?.body_region_focus) q = q.eq("body_region_focus", filters.body_region_focus);
  if (filters?.intro_video_id) q = q.eq("intro_video_id", filters.intro_video_id);
  if (filters?.lineup_video_id) {
    // Contains any lineup object with this video ref (may also match exercise UUID collisions — rare).
    q = q.contains("lineup_items", [{ type: "video", ref_id: filters.lineup_video_id }]);
  }
  if (filters?.curriculum_phase_id) q = q.eq("curriculum_phase_id", filters.curriculum_phase_id);
  if (!filters?.include_archived && applyArchivedAtNull) q = q.is("archived_at", null);
  return q;
}

export async function listBundles(filters?: ListBundlesFilters): Promise<QuarterlyBundleRow[]> {
  const wantActiveOnly = !filters?.include_archived;
  let { data, error } = await buildListBundlesQuery(filters, wantActiveOnly);
  if (error && wantActiveOnly && isMissingArchivedAtColumnError(error)) {
    if (import.meta.env.DEV) {
      console.warn(
        "[listBundles] quarterly_bundles.archived_at missing — apply migration 20260507120000_curriculum_redesign.sql. Retrying without archived filter.",
      );
    }
    const retry = buildListBundlesQuery(filters, false);
    ({ data, error } = await retry);
    if (!error && data) {
      data = data.filter((row) => (row as QuarterlyBundleRow).archived_at == null);
    }
  }
  if (error && filters?.lineup_video_id && isMissingLineupItemsColumnError(error)) {
    // Pre-migration: fall back to intro_video_id filter only.
    const { lineup_video_id, ...rest } = filters;
    return listBundles({ ...rest, intro_video_id: lineup_video_id });
  }
  if (error) throw error;
  return ((data ?? []) as QuarterlyBundleRow[]).map(hydrateBundleRow);
}

export async function getBundle(id: string): Promise<QuarterlyBundleRow | null> {
  const { data, error } = await supabase
    .from("quarterly_bundles")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return hydrateBundleRow(data as QuarterlyBundleRow);
}

export interface CreateBundleInput {
  organization_id: string;
  job_category_id: string | null;
  quarter: string;
  title: string;
  body_region_focus: BodyRegionFocus | null;
  /** Preferred: mixed ordered line-up. When omitted, built from intro_video_id + exercise_ids. */
  lineup_items?: BundleLineupItem[];
  intro_video_id?: string | null;
  exercise_ids?: string[];
  template_id: string | null;
  status?: BundleStatus;
  created_by: string | null;
  /** Set when this bundle is created inline by the curriculum phase wizard. */
  curriculum_phase_id?: string | null;
}

function payloadFromCreateInput(input: CreateBundleInput): Record<string, unknown> {
  const lineup =
    input.lineup_items && input.lineup_items.length > 0
      ? normalizeLineup(input.lineup_items)
      : buildLineupFromLegacy(input.intro_video_id ?? null, input.exercise_ids ?? []);
  const legacy = deriveLegacyFromLineup(lineup);
  return {
    organization_id: input.organization_id,
    job_category_id: input.job_category_id,
    quarter: input.quarter,
    title: input.title,
    body_region_focus: input.body_region_focus,
    template_id: input.template_id,
    status: input.status ?? "draft",
    created_by: input.created_by,
    curriculum_phase_id: input.curriculum_phase_id ?? null,
    lineup_items: lineup,
    intro_video_id: legacy.intro_video_id,
    exercise_ids: legacy.exercise_ids,
  };
}

export async function createBundle(input: CreateBundleInput): Promise<QuarterlyBundleRow> {
  const payload = payloadFromCreateInput(input);
  let { data, error } = await supabase
    .from("quarterly_bundles")
    .insert(payload as never)
    .select()
    .single();

  if (error && isMissingLineupItemsColumnError(error)) {
    const legacyOnly = { ...payload };
    delete legacyOnly.lineup_items;
    ({ data, error } = await supabase
      .from("quarterly_bundles")
      .insert(legacyOnly as never)
      .select()
      .single());
  }

  if (error) throw error;
  if (!data) throw new Error("Failed to create bundle");
  return hydrateBundleRow(data as QuarterlyBundleRow);
}

export type UpdateBundlePatch = Partial<
  Pick<
    QuarterlyBundleRow,
    | "title"
    | "body_region_focus"
    | "lineup_items"
    | "intro_video_id"
    | "exercise_ids"
    | "template_id"
    | "status"
    | "public_token"
    | "publish_date"
    | "published_at"
    | "ai_rationale"
    | "job_category_id"
    | "curriculum_phase_id"
  >
>;

function syncPatchLineup(patch: UpdateBundlePatch): UpdateBundlePatch {
  if (patch.lineup_items) {
    const lineup = normalizeLineup(patch.lineup_items);
    const legacy = deriveLegacyFromLineup(lineup);
    return {
      ...patch,
      lineup_items: lineup,
      intro_video_id: legacy.intro_video_id,
      exercise_ids: legacy.exercise_ids,
    };
  }
  // Legacy-only writers: rebuild lineup from whatever they set.
  if (patch.intro_video_id !== undefined || patch.exercise_ids !== undefined) {
    // Caller may only patch one side — incomplete without current row. Leave as-is;
    // BundleWizard / curriculum should pass lineup_items explicitly.
    if (patch.intro_video_id !== undefined && patch.exercise_ids !== undefined) {
      const lineup = buildLineupFromLegacy(patch.intro_video_id, patch.exercise_ids);
      return { ...patch, lineup_items: lineup };
    }
  }
  return patch;
}

export async function updateBundle(
  id: string,
  patch: UpdateBundlePatch,
): Promise<QuarterlyBundleRow> {
  const synced = syncPatchLineup(patch);
  let { data, error } = await supabase
    .from("quarterly_bundles")
    .update(synced as never)
    .eq("id", id)
    .select()
    .single();

  if (error && isMissingLineupItemsColumnError(error) && synced.lineup_items) {
    const legacyOnly = { ...synced };
    delete legacyOnly.lineup_items;
    ({ data, error } = await supabase
      .from("quarterly_bundles")
      .update(legacyOnly as never)
      .eq("id", id)
      .select()
      .single());
  }

  if (error) throw error;
  if (!data) throw new Error("Failed to update bundle");
  return hydrateBundleRow(data as QuarterlyBundleRow);
}

export async function deleteBundle(id: string): Promise<void> {
  const { error } = await supabase.from("quarterly_bundles").delete().eq("id", id);
  if (error) throw error;
}

export interface BundlePlayMetrics {
  sessions_started: number;
  sessions_completed: number;
  completion_rate: number; // 0..100
  last_played_at: string | null;
}

/**
 * Bundle matching for an employee: category-specific bundle wins over the org-wide
 * fallback. Filters out archived bundles. Returns the most recently-published row.
 *
 * NOTE: prefer resolving bundles via `curriculum_phase_id` (`employeeCurriculum.ts`). This helper
 * remains for legacy Studio/admin surfaces that still query published bundles without curriculum linkage.
 */
export async function findActiveBundleForEmployee(
  orgId: string,
  categoryId: string | null,
): Promise<QuarterlyBundleRow | null> {
  const FETCH_LIMIT = 64;

  async function pickCategoryBundle(): Promise<QuarterlyBundleRow | null> {
    const withArchived = () =>
      supabase
        .from("quarterly_bundles")
        .select("*")
        .eq("organization_id", orgId)
        .eq("job_category_id", categoryId!)
        .eq("status", "published")
        .is("archived_at", null)
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();
    const { data, error } = await withArchived();
    if (error && isMissingArchivedAtColumnError(error)) {
      const { data: rows, error: err2 } = await supabase
        .from("quarterly_bundles")
        .select("*")
        .eq("organization_id", orgId)
        .eq("job_category_id", categoryId!)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(FETCH_LIMIT);
      if (err2) throw err2;
      const row = (rows ?? []).find((r) => (r as QuarterlyBundleRow).archived_at == null) ?? null;
      return row ? hydrateBundleRow(row as QuarterlyBundleRow) : null;
    }
    if (error) throw error;
    return data ? hydrateBundleRow(data as QuarterlyBundleRow) : null;
  }

  async function pickOrgWideBundle(): Promise<QuarterlyBundleRow | null> {
    const withArchived = () =>
      supabase
        .from("quarterly_bundles")
        .select("*")
        .eq("organization_id", orgId)
        .is("job_category_id", null)
        .eq("status", "published")
        .is("archived_at", null)
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();
    const { data, error } = await withArchived();
    if (error && isMissingArchivedAtColumnError(error)) {
      const { data: rows, error: err2 } = await supabase
        .from("quarterly_bundles")
        .select("*")
        .eq("organization_id", orgId)
        .is("job_category_id", null)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(FETCH_LIMIT);
      if (err2) throw err2;
      const row = (rows ?? []).find((r) => (r as QuarterlyBundleRow).archived_at == null) ?? null;
      return row ? hydrateBundleRow(row as QuarterlyBundleRow) : null;
    }
    if (error) throw error;
    return data ? hydrateBundleRow(data as QuarterlyBundleRow) : null;
  }

  if (categoryId) {
    const hit = await pickCategoryBundle();
    if (hit) return hit;
  }
  return pickOrgWideBundle();
}

/**
 * Phase-end archival. Sets archived_at = NOW() on every active bundle owned by the given phases.
 * Idempotent: archived rows are skipped. See plan §6.3 lazy phase transition.
 */
export async function archiveBundlesForPhases(curriculumPhaseIds: string[]): Promise<void> {
  if (curriculumPhaseIds.length === 0) return;
  const { error } = await supabase
    .from("quarterly_bundles")
    .update({ archived_at: new Date().toISOString() } as never)
    .in("curriculum_phase_id", curriculumPhaseIds)
    .is("archived_at", null);
  if (error) throw error;
}

/**
 * Past published/archived bundles for an employee's history view (Phase 2).
 * Excludes the active bundle when supplied.
 */
export async function listEmployeeBundleHistory(
  orgId: string,
  excludeBundleId?: string,
): Promise<QuarterlyBundleRow[]> {
  let q = supabase
    .from("quarterly_bundles")
    .select("*")
    .eq("organization_id", orgId)
    .in("status", ["published", "archived"])
    .order("published_at", { ascending: false });
  if (excludeBundleId) q = q.neq("id", excludeBundleId);
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as QuarterlyBundleRow[]).map(hydrateBundleRow);
}

export async function getBundlePlayMetrics(bundleId: string): Promise<BundlePlayMetrics> {
  const { data, error } = await supabase
    .from("bundle_plays")
    .select("started_at, completed_at")
    .eq("bundle_id", bundleId);
  if (error) {
    console.warn("[getBundlePlayMetrics]", bundleId, formatSupabaseAccessError(error));
    throw error;
  }
  const rows = (data ?? []) as Array<{ started_at: string; completed_at: string | null }>;
  const total = rows.length;
  const completed = rows.filter((r) => r.completed_at !== null).length;
  let last: string | null = null;
  for (const r of rows) {
    const ts = r.completed_at ?? r.started_at;
    if (!last || ts > last) last = ts;
  }
  return {
    sessions_started: total,
    sessions_completed: completed,
    completion_rate: total === 0 ? 0 : Math.round((completed / total) * 100),
    last_played_at: last,
  };
}
